import React, { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { DatabaseBackup, Download, UploadCloud, RotateCcw, Loader2, Clock, CheckCircle2, AlertTriangle, Cloud, Bell } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Btn, Panel, Badge, Loading } from "@/components/common";

export default function BackupRecovery({ workspaceId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [restoreInfo, setRestoreInfo] = useState(null);
  const [dropbox, setDropbox] = useState(null);
  const fileRef = useRef();
  const load = useCallback(async () => {
    const [{ data }, dbx] = await Promise.all([
      api.get(`/backups?workspace_id=${workspaceId}`),
      api.get(`/dropbox/status?workspace_id=${workspaceId}`).catch(() => ({ data: null })),
    ]);
    setData(data); setDropbox(dbx.data);
  }, [workspaceId]);
  useEffect(() => { load(); }, [load]);

  const backupNow = async () => {
    setBusy(true);
    try { await api.post(`/backups?workspace_id=${workspaceId}`); toast.success("Backup complete"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Backup failed"); }
    finally { setBusy(false); }
  };
  const download = (id) => window.open(`${api.defaults.baseURL}/backups/${id}/download?workspace_id=${workspaceId}`, "_blank");
  const toggleSchedule = async (enabled) => { await api.put(`/backups/schedule?workspace_id=${workspaceId}`, { enabled }); load(); };
  const connectDropbox = async () => {
    try { const { data } = await api.get(`/dropbox/oauth/start?workspace_id=${workspaceId}`); window.location.assign(data.authorize_url); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const retryDropbox = async (id) => {
    setBusy(true);
    try { await api.post(`/backups/${id}/dropbox?workspace_id=${workspaceId}`); toast.success("Off-site upload verified"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Off-site upload failed (local copy preserved)"); load(); }
    finally { setBusy(false); }
  };
  const onPick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: info } = await api.post(`/restore/validate?workspace_id=${workspaceId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const reader = new FileReader();
      reader.onload = () => setRestoreInfo({ ...info, b64: reader.result.split(",")[1] });
      reader.readAsDataURL(file);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Invalid package"); }
    finally { setBusy(false); fileRef.current.value = ""; }
  };
  const doRestore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/restore?workspace_id=${workspaceId}`, { package_b64: restoreInfo.b64, mode: "isolated" });
      toast.success(`Restored into a new isolated workspace (${Object.values(data.restored_counts || {}).reduce((a, b) => a + b, 0)} records, ${data.attachments_restored} files). Switch workspaces to inspect it.`);
      setRestoreInfo(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Restore failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <Loading />;
  const sIcon = { complete: CheckCircle2, running: Loader2, failed: AlertTriangle, incomplete: AlertTriangle };
  const sColor = { complete: "confirmed", running: "candidate", failed: "disputed", incomplete: "uncertain" };
  return (
    <div className="mb-8" data-testid="backup-section">
      <div className="si-label flex items-center gap-1.5 mb-2"><DatabaseBackup size={13} /> Backup & recovery</div>
      <Panel className="p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-sm" style={{ color: "var(--si-text)" }}>Encrypted full backups (records + attachments + histories + permissions).</div>
            <div className="si-label mt-1">Last successful: {data.last_successful ? new Date(data.last_successful.completed_at || data.last_successful.created_at).toLocaleString() : "never"} · Retention {data.schedule?.retention?.daily || 30} daily / {data.schedule?.retention?.monthly || 12} monthly</div>
          </div>
          <div className="flex gap-2">
            <Btn variant="primary" onClick={backupNow} disabled={busy} data-testid="backup-now">{busy ? <Loader2 size={14} className="animate-spin" /> : <DatabaseBackup size={14} />} Back up now</Btn>
            <Btn variant="secondary" onClick={() => fileRef.current.click()} disabled={busy} data-testid="restore-pick"><UploadCloud size={14} /> Restore from file</Btn>
            <input ref={fileRef} type="file" accept=".sibak" className="hidden" onChange={onPick} data-testid="restore-input" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm mt-3" style={{ color: "var(--si-text)" }}>
          <input type="checkbox" checked={data.schedule?.enabled !== false} onChange={(e) => toggleSchedule(e.target.checked)} data-testid="backup-schedule-toggle" />
          <Clock size={13} /> Daily automatic backups (next run ~03:30 UTC)
        </label>
        <div className="text-xs mt-2" style={{ color: "var(--si-text-2)" }}>{data.key_recovery}</div>
      </Panel>

      {/* Dropbox — off-site copy (status shown separately from local) */}
      <Panel className="p-4 mt-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--si-text)" }}>
            <Cloud size={14} /> Dropbox off-site copy
            <Badge value={dropbox?.connected ? "confirmed" : "candidate"}>{dropbox?.connected ? "connected" : dropbox?.configured ? "not authorized" : "not connected"}</Badge>
          </div>
          <Btn variant="secondary" className="!py-1 !px-3 text-xs" onClick={connectDropbox} disabled={!dropbox?.configured} data-testid="dropbox-connect"><Cloud size={13} /> Connect Dropbox</Btn>
        </div>
        <div className="text-xs mt-2 space-y-0.5" style={{ color: "var(--si-text-2)" }}>
          {dropbox?.connected && <div data-testid="dropbox-account">Connected account: <span className="font-mono">{dropbox?.account_id || "authorized"}</span> · Destination: <span className="font-mono">{dropbox?.destination}</span></div>}
          <div>Automatic upload after every successful backup: <strong>{dropbox?.automatic_upload ? "on" : "off"}</strong> (activates once connected). Local object-storage recovery point is always kept even if an off-site upload fails.</div>
          <div>Last verified off-site copy: {dropbox?.last_verified_offsite ? new Date(dropbox.last_verified_offsite.dropbox_verified_at).toLocaleString() : "none yet"} · pending {dropbox?.pending_uploads ?? 0} · failed {dropbox?.failed_uploads ?? 0}</div>
          {dropbox?.callback_url && <div>OAuth callback URL: <span className="font-mono">{dropbox.callback_url}</span></div>}
          {dropbox?.required_scopes && <div>Required scopes: <span className="font-mono">{dropbox.required_scopes.join(", ")}</span></div>}
        </div>
        {dropbox?.remaining_step && <div className="text-xs mt-2" style={{ color: "var(--si-warning)" }}>Remaining step: {dropbox.remaining_step}</div>}
      </Panel>

      {/* History */}
      <Panel className="mt-3">
        <div className="si-label px-5 py-2.5" style={{ borderBottom: "1px solid var(--si-border)" }}>Backup history</div>
        {data.backups.length === 0 ? <div className="text-sm px-5 py-4" style={{ color: "var(--si-text-2)" }}>No backups yet.</div> : data.backups.map((b) => {
          const Ic = sIcon[b.status] || Clock;
          return (
            <div key={b.id} className="flex items-center justify-between px-5 py-2.5" style={{ borderBottom: "1px solid var(--si-border)" }} data-testid={`backup-${b.id}`}>
              <div>
                <div className="text-sm flex items-center gap-2" style={{ color: "var(--si-text)" }}><Ic size={13} /> {new Date(b.created_at).toLocaleString()} <Badge value={sColor[b.status] || "candidate"}>{b.status}</Badge> <span className="si-label">{b.kind}</span>{b.status === "complete" && b.dropbox_status && b.dropbox_status !== "not_connected" && <Badge value={b.dropbox_verified ? "confirmed" : b.dropbox_status === "failed" ? "disputed" : "candidate"}>off-site: {b.dropbox_verified ? "verified" : b.dropbox_status}</Badge>}</div>
                <div className="si-label mt-0.5">{b.status === "complete" ? `${Object.values(b.record_counts || {}).reduce((a, c) => a + c, 0)} records · ${b.total_attachments || 0} files · ${Math.round((b.size || 0) / 1024)} KB${b.dropbox_error ? " · off-site error: " + b.dropbox_error : ""}` : (b.error || "")}</div>
              </div>
              <div className="flex items-center gap-1">
                {b.status === "complete" && dropbox?.connected && !b.dropbox_verified && <Btn variant="ghost" className="!py-1 !px-2 text-xs" onClick={() => retryDropbox(b.id)} disabled={busy} data-testid={`backup-dbx-retry-${b.id}`}><Cloud size={13} /> {b.dropbox_status === "failed" ? "Retry off-site" : "Upload off-site"}</Btn>}
                {b.status === "complete" && <Btn variant="ghost" className="!py-1 !px-2 text-xs" onClick={() => download(b.id)} data-testid={`backup-dl-${b.id}`}><Download size={13} /> Download</Btn>}
              </div>
            </div>
          );
        })}
      </Panel>

      {restoreInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setRestoreInfo(null)}>
          <div className="w-full max-w-md p-5" style={{ background: "var(--si-surface)", border: "1px solid var(--si-border)", borderRadius: 4 }} onClick={(e) => e.stopPropagation()} data-testid="restore-modal">
            <div className="si-label mb-3 flex items-center gap-1.5"><RotateCcw size={13} /> Restore backup</div>
            {!restoreInfo.valid ? (
              <div className="text-sm" style={{ color: "var(--si-danger)" }}>Package failed validation:<ul className="mt-1 list-disc ml-4">{(restoreInfo.issues || []).map((i, n) => <li key={n}>{i}</li>)}</ul></div>
            ) : (
              <>
                <div className="text-sm mb-2" style={{ color: "var(--si-text)" }}>Backup of <strong>{restoreInfo.workspace_name}</strong></div>
                <div className="text-xs space-y-0.5 mb-3" style={{ color: "var(--si-text-2)" }}>
                  {Object.entries(restoreInfo.counts || {}).filter(([, v]) => v > 0).map(([k, v]) => <div key={k}>{k}: {v}</div>)}
                  <div>attachments: {restoreInfo.total_attachments}</div>
                </div>
                <div className="text-xs p-2 mb-3" style={{ background: "var(--si-bg)", borderRadius: 2, color: "var(--si-text-2)" }}>This restores into a <strong>new isolated workspace</strong> for safe inspection. Your current data is untouched. Canonical links, histories, attachments and owner-only restrictions are preserved; no new access is granted.</div>
                <div className="flex justify-end gap-2">
                  <Btn variant="ghost" onClick={() => setRestoreInfo(null)}>Cancel</Btn>
                  <Btn variant="primary" onClick={doRestore} disabled={busy} data-testid="restore-confirm">{busy ? "Restoring…" : "Restore to new workspace"}</Btn>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function NudgeSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/nudge-settings`).then((r) => setS(r.data)).catch(() => setS({ enabled: true, timezone: "UTC" })); }, []);
  const save = async (patch) => {
    const next = { ...s, ...patch }; setS(next);
    const tz = next.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    await api.put(`/nudge-settings`, { enabled: next.enabled, timezone: tz });
    toast.success("Saved");
  };
  if (!s) return null;
  return (
    <div className="mb-8" data-testid="nudge-settings">
      <div className="si-label flex items-center gap-1.5 mb-2"><Bell size={13} /> Commitment reminders</div>
      <Panel className="p-4">
        <label className="flex items-center gap-2 text-sm" style={{ color: "var(--si-text)" }}>
          <input type="checkbox" checked={s.enabled !== false} onChange={(e) => save({ enabled: e.target.checked, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone })} data-testid="nudge-enabled" />
          Nudge me one day before an approved commitment is due (your timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone})
        </label>
      </Panel>
    </div>
  );
}
